import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'

export default function useConversations() {
  const [conversations, setConversations] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchConversations = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await axios.get('/api/conversations')
      setConversations(response.data.data || [])
    } catch (err) {
      console.error('Error fetching conversations:', err)
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchConversations()
  }, [fetchConversations])

  const createConversation = useCallback(async (otherUserId) => {
    try {
      const response = await axios.post('/api/conversations', {
        other_user_id: otherUserId
      })
      const newConversation = response.data.data

      setConversations((prev) => {
        const existingIndex = prev.findIndex((c) => c.id === newConversation.id)

        if (existingIndex >= 0) {
          const updated = [...prev]
          updated[existingIndex] = newConversation
          updated.unshift(updated.splice(existingIndex, 1)[0])
          return updated
        }

        return [newConversation, ...prev]
      })

      return newConversation
    } catch (err) {
      console.error('Error creating conversation:', err)
      throw err
    }
  }, [])

  const archiveConversation = useCallback(async (conversationId) => {
    try {
      await axios.post(`/api/conversations/${conversationId}/archive`)
      setConversations((prev) => prev.filter((c) => c.id !== conversationId))
    } catch (err) {
      console.error('Error archiving conversation:', err)
      throw err
    }
  }, [])

  const updateConversation = useCallback((conversationId, updates) => {
    setConversations((prev) =>
      prev.map((conversation) => (conversation.id === conversationId ? { ...conversation, ...updates } : conversation))
    )
  }, [])

  const addConversation = useCallback((newConversation) => {
    setConversations((prev) => {
      const exists = prev.find((c) => c.id === newConversation.id)
      if (exists) {
        return prev.map((c) => (c.id === newConversation.id ? newConversation : c))
      }
      return [newConversation, ...prev]
    })
  }, [])

  const reorderConversation = useCallback((conversationId) => {
    setConversations((prev) => {
      const index = prev.findIndex((c) => c.id?.toString() === conversationId?.toString())
      if (index > 0) {
        const updated = [...prev]
        const [conversation] = updated.splice(index, 1)
        updated.unshift(conversation)
        return updated
      }
      return prev
    })
  }, [])

  const fetchArchivedConversations = useCallback(async () => {
    try {
      const response = await axios.get('/api/conversations/archived')
      return response.data.data || []
    } catch (err) {
      console.error('Error fetching archived conversations:', err)
      throw err
    }
  }, [])

  const unarchiveConversation = useCallback(
    async (conversationId) => {
      try {
        await axios.post(`/api/conversations/${conversationId}/unarchive`)
        await fetchConversations()
      } catch (err) {
        console.error('Error unarchiving conversation:', err)
        throw err
      }
    },
    [fetchConversations]
  )

  return {
    conversations,
    isLoading,
    error,
    fetchConversations,
    createConversation,
    archiveConversation,
    updateConversation,
    addConversation,
    reorderConversation,
    fetchArchivedConversations,
    unarchiveConversation
  }
}
